import React, { useRef, useEffect, useCallback, memo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container } from "@pixi/react";
import { Settings } from "../../../settings";
import MainReelsetBackground from "../MainReelsetBackground/MainReelsetBackground";
import { setLinesRotationFinished } from "../../../State/Slices/spinSlice";
import { SOUND_NAME } from "../../../sounds";
import { playSound } from "../../../State/Slices/soundsSlice";
import MainReel from "../MainReel/MainReel";
import MainReelsetController from "./MainReelsetController";

const MainReelSet = ({ dimensions, symbolSize, reelRefs }) => {
  const reelsetController = useRef(null);
  const dispatch = useDispatch();
  const { spinComplete, counterAnimationFinished, isSpinning } = useSelector(
    (state) => state.spin
  );
  const { currentRound, winningLines } = useSelector((state) => state.game);
  const { isFreeSpinMode } = useSelector((state) => state.freeSpins);

  const handleSetLinesRotation = useCallback(
    (linesRotationFinished) => {
      if (isFreeSpinMode) return;
      console.log("🔄 Setting linesRotationFinished:", linesRotationFinished);
      dispatch(setLinesRotationFinished(linesRotationFinished));
    },
    [dispatch, isFreeSpinMode]
  );

  const handleSetCurrentWinningLine = useCallback(
    (currentWinningLine) => {
      if (
        isFreeSpinMode ||
        currentWinningLine === null ||
        !counterAnimationFinished
      )
        return;
      console.log("🔊 Playing winning line sound:", currentWinningLine);
      dispatch(playSound(SOUND_NAME[`winningLine${currentWinningLine}`]));
    },
    [dispatch, counterAnimationFinished, isFreeSpinMode]
  );

  useEffect(() => {
    if (isFreeSpinMode) return;

    if (
      reelsetController.current &&
      spinComplete &&
      Object.keys(currentRound?.wins || {}).length > 0
    ) {
      console.log("🏆 Starting winning lines animation.");
      const winningData =
        reelsetController.current?.matchHorizontalWinningLines(
          winningLines,
          currentRound?.wins
        );
      reelsetController.current?.startWinningLinesAnimation(winningData);
    }
  }, [spinComplete, winningLines, currentRound, isFreeSpinMode]);

  useEffect(() => {
    if (isFreeSpinMode || !isSpinning) return;

    console.log("🛑 Stopping winning lines animation due to new spin.");
    reelsetController.current?.stopWinningLinesAnimation();
  }, [isSpinning, isFreeSpinMode]);

  useEffect(() => {
    if (isFreeSpinMode) return;

    if (reelRefs.current && !reelsetController.current) {
      console.log("⚙️ Initializing MainReelsetController.");
      reelsetController.current = new MainReelsetController(
        reelRefs.current,
        handleSetLinesRotation,
        handleSetCurrentWinningLine
      );

      reelRefs.current.forEach((reel, index) => {
        reelsetController.current.reelControllers[index] =
          reel.getReelController();
      });
    }
  }, [
    reelRefs,
    handleSetLinesRotation,
    handleSetCurrentWinningLine,
    isFreeSpinMode,
  ]);

  return (
    <MainReelsetBackground dimensions={dimensions} symbolSize={symbolSize}>
      {Settings.REELS.map((reel, index) => (
        <Container
          key={index}
          name={"ReelContainer"}
          x={symbolSize * (index - 2)}
          y={-symbolSize}
          zIndex={3}
          sortableChildren
        >
          <MainReel
            handleSetCurrentWinningLine={handleSetCurrentWinningLine}
            ref={(el) => {
              reelRefs.current[index] = el;
            }}
            reel={reel}
            symbolSize={symbolSize}
            index={index}
          />
        </Container>
      ))}
    </MainReelsetBackground>
  );
};

export default memo(MainReelSet);
